import styles from './parcelsPage.module.css';
import cn from 'classnames';
import ExcelExportModalWindow from './modals/ExcelExportModalWindow/ExcelExportModalWindow';
import FilterIcon from '@assets/icons/filter.svg';
import NewTab from './tabs/NewTab.tsx';
import NoItems from '@components/NoItems';
import Page from '@components/Page/Page.tsx';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import { AddSellerPanel } from '@components/AddSeller';
import { AxiosResponse } from 'axios';
import { buildFilterObject } from '@utils/buildFilterObject';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import {
  getParcels,
  getParcelsXLSXFileByEmail,
  getParcelsXLSXFile,
  useHasParcels,
  ParcelPageItem
} from '@api/apiClient';
import { Loader } from '@components/Loader';
import { Modal } from '@components/Modal';
import { ReactElement, useState } from 'react';
import { saveAs } from 'file-saver';
import { TabsList, TabsContent } from '@components/Tabs/Tabs.tsx';
import { useQuery } from 'react-query';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useTranslation } from 'react-i18next';

const ParcelsPage = () => {
  const [activeTab, setActiveTab] = useState<string>('NewTab');
  const [currentNumberParcels, setCurrentNumberParcels] = useState<number>(0);
  const [detailId, setDetailId] = useState<string>('');
  const [errorFileDownloading, setErrorFileDownloading] = useState<string | undefined>(undefined);
  const [excelExportFilters, setExcelExportFilters] = useState<FilterModel[]>([]);
  const [initialNumberParcels, setInitialNumberParcels] = useState<number>(0);
  const [isFileDownloading, setIsFileDownloading] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isOpenExcelExportWindow, setIsOpenExcelExportWindow] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);

  const { isFetching, sellersNotFound } = useSellersCounter();
  const { hasNoParcels: noData } = useHasParcels(!sellersNotFound);
  const { t } = useTranslation();

  const limitationExcelExport: number = 10000;

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  };

  const onOpenParcel = (id: string) => {
    setIsOpenParcelWindow(true);
    setDetailId(id);
  };

  const handlerTableDataUpdate = (_: ParcelPageItem[], total: number) => {
    setCurrentNumberParcels(total);
  };

  const { data: parcelsTotal } = useQuery({
    queryKey: [
      'parcels-page',
      { page: 1, pageSize: 1 },
      []
    ],
    queryFn: () => getParcels({
      page: { page: 1, pageSize: 1 },
      filters: []
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: !sellersNotFound
  });

  const tabs = [
    {
      id: 'NewTab',
      title: t('parcelsPage.tabAll'),
      content: (
          <NewTab
              handlerSetExcelExportFilters={setExcelExportFilters}
              handlerTableDataUpdate={handlerTableDataUpdate}
              isFilterOpen={isFilterOpen}
              onOpenParcel={onOpenParcel}
              pending={isFetching}
              sellersNotFound={sellersNotFound}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: parcelsTotal
    }
    // {
    //   id: "InDeliveryTab",
    //   title: t("parcelsPage.tabInDelivery"),
    //   content: <NewTab sellers={sellersFilter} pending={isFetching} />,
    // },
    // {
    //   id: "DelivevredTab",
    //   title: t("parcelsPage.tabDelivevred"),
    //   content: <NewTab sellers={sellersFilter} pending={isFetching} />,
    // },
    // {
    //   id: "CancelledTab",
    //   title: t("parcelsPage.tabCancelled"),
    //   content: <NewTab sellers={sellersFilter} pending={isFetching} />,
    // },
  ];

  const downloadingExcelFile = (filters: FilterModel[]): void => {
    setIsFileDownloading(true);
    const emailFilter: FilterModel | undefined = filters.find((item: FilterModel) => item.name === 'recipientEmail');
    const downloadFunc = emailFilter ? getParcelsXLSXFileByEmail : getParcelsXLSXFile;
    downloadFunc(buildFilterObject(filters))
        .then((response: AxiosResponse) => {
          if (!(initialNumberParcels >= limitationExcelExport)) {
            const contentDisposition = response.headers['content-disposition'];
            const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")(.*?)(?:;|$|")/);
            let fileName: string = 'parcels.xlsx';
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = decodeURIComponent(fileNameMatch[1]);
            }
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            saveAs(blob, fileName);
            setIsOpenExcelExportWindow(false);
          }
        })
        .catch((e) => setErrorFileDownloading(e))
        .finally(() => setIsFileDownloading(false));
  };

  const handlerExcelExport = (): void => {
    if (currentNumberParcels >= limitationExcelExport) {
      setIsOpenExcelExportWindow(true);
    } else {
      downloadingExcelFile(excelExportFilters);
    }
  };

  const currentAction: ReactElement | null =
      isFileDownloading
          ? <div className={styles.excelExportPlate}>
            <Loader
                dark
                size="xs"
            />
            {t('parcelsPage.excelExportPlate')}
          </div>
          : null;

  return (
      <Page
          actions={currentAction}
          title={t('parcelsPage.title')}
      >
        <div className={styles.tabsRow}>
          <TabsList
              tabs={tabs}
              onChange={setActiveTab}
              value={activeTab}
          />
          <div className={styles.filters}>
            <Button
                className={styles.excelExportBtn}
                disabled={!currentNumberParcels || isFileDownloading}
                onClick={handlerExcelExport}
                type={ButtonType.SKELETON}
                title={t('parcelsPage.excelExportBtn')}
            />
            <Button
                className={cn(isFilterOpen ? styles.filterBtnActive : styles.filterBtnInactive)}
                icon={FilterIcon}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                size={ButtonSize.S}
                type={ButtonType.ROUNDED}
                title={t('filters.filters')}
            />
          </div>
        </div>
        {sellersNotFound ? (
            <AddSellerPanel isFirstSeller/>
        ) : (
            noData ? (
                <NoItems
                    title={t('noItems.parcels.title')}
                    description={t('noItems.parcels.description')}
                />
            ) : (
                <TabsContent
                    tab={tabs.find((tab) => (tab.id === activeTab))}
                />
            )
        )}
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={closeParcelWindow}
            onClose={closeParcelWindow}
            width={900}
        >
          <ParcelModalWindow parcelId={detailId}/>
        </Modal>
        <Modal
            isOpen={isOpenExcelExportWindow}
            onClickOverlay={() => setIsOpenExcelExportWindow(false)}
            onClose={() => setIsOpenExcelExportWindow(false)}
            width={650}
        >
          <ExcelExportModalWindow
              errorFileDownloading={errorFileDownloading}
              excelExportFilters={excelExportFilters}
              handlerDownloadingExcelFile={downloadingExcelFile}
              handlerSetErrorFileDownloading={setErrorFileDownloading}
              handlerSetInitialNumberParcels={setInitialNumberParcels}
              handlerSetIsOpenExcelExportWindow={setIsOpenExcelExportWindow}
              isFileDownloading={isFileDownloading}
              limitationExcelExport={limitationExcelExport}
          />
        </Modal>
      </Page>
  );
};

export default ParcelsPage;
